import React, { useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet } from 'react-native';
import groupBy from 'lodash/groupBy';
import { DNABox, DNAButton, DNAIcon, DNAModal, DNAText, RadioItem } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAModalVariant } from 'src/components/DNA/Modal/DNAConnectedModal';
import DNACustomDeckThumbnail from 'src/components/DNA/Document/DNACustomDeckThumbnail';
import { sorts } from 'src/state/redux/folder/query';
import { sortCollection } from 'src/state/redux/selector/common';
import type { FolderORM } from 'src/types/orms';
import type { AISearchSlideReference } from 'src/utils/customDeckRules/aiSearch';
import { getAISearchDeckUnavailableReason, useAISearchCustomDeck } from './useAISearchCustomDeck';

const styles = StyleSheet.create({
  modal: {
    width: 600,
    minWidth: 600,
    maxWidth: 600,
    height: 684,
    minHeight: 684,
    maxHeight: 684,
  },
  body: {
    minHeight: 0,
    padding: 16,
  },
  row: {
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors['color-ui2-neutral-200'],
  },
  folderTitle: {
    minWidth: 0,
  },
  staticContent: {
    paddingBottom: 16,
  },
  folderList: {
    minHeight: 0,
  },
  folderListContent: {
    paddingBottom: 16,
  },
  deckRow: {
    marginLeft: 16,
  },
  deckThumbnail: {
    marginRight: 16,
  },
  deckTitleContainer: {
    minWidth: 0,
    marginRight: 16,
  },
  deckSelection: {
    marginRight: 10,
  },
  confirmation: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
});

export const AddToCustomPresentationModal: DNAModalVariant<{
  references: readonly AISearchSlideReference[];
}> = ({ references, closeModal }) => {
  const state = useAISearchCustomDeck(references, closeModal);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleFolder = (id: string) => setExpanded(previous => {
    const next = new Set(previous);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const { roots, childrenByFolder, decksByFolder } = useMemo(() => {
    const sortedFolders = sortCollection(state.folders, sorts.pinnedAsc);
    const folderIds = new Set(sortedFolders.map(folder => folder.model.id));
    return {
      roots: sortedFolders.filter(folder =>
        !folder.relations.parentFolderORM || !folderIds.has(folder.relations.parentFolderORM.model.id)),
      childrenByFolder: groupBy(sortedFolders, folder => folder.relations.parentFolderORM?.model.id),
      decksByFolder: groupBy(state.targets, target => target.folder.model.id),
    };
  }, [state.folders, state.targets]);

  const renderFolder = (folder: FolderORM, ancestors: Set<string>): React.ReactNode => {
    if (ancestors.has(folder.model.id)) return null;
    const path = new Set([...ancestors, folder.model.id]);
    const open = expanded.has(folder.model.id);
    const children = childrenByFolder[folder.model.id] ?? [];
    const decks = decksByFolder[folder.model.id] ?? [];
    return (
      <DNABox key={folder.model.id} appearance="col" spacing="sm" style={{ paddingLeft: ancestors.size ? 16 : 0 }}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={folder.meta.title}
          accessibilityState={{ expanded: open }}
          aria-expanded={open}
          onPress={() => toggleFolder(folder.model.id)}
        >
          <DNABox
            appearance="row"
            alignY="center"
            spacing="md"
            childFill={2}
            childStyle={[2, styles.folderTitle]}
            style={styles.row}
          >
            <DNAIcon.Styled name={open ? 'caret-down' : 'caret-right'} size="sm" />
            <DNAIcon.Styled
              name="folder-simple-fill"
              color={colors['color-ui2-brand-200']}
              width={32}
              height={32}
            />
            <DNAText b2 numberOfLines={1} ellipsizeMode="tail">{folder.meta.title}</DNAText>
            {folder.meta.isFavorite && <DNAIcon.Styled name="star-fill" status="primary" size="sm" />}
          </DNABox>
        </Pressable>
        {open && children.map(child => renderFolder(child, path))}
        {open && decks.map(target => {
          const reason = getAISearchDeckUnavailableReason(target, state.userId, state.isOnline);
          const selected = target.item.model.id === state.selectedItemId;
          const disabled = !!reason || state.preparing;
          const select = () => state.setSelectedItemId(target.item.model.id);
          return (
            <Pressable
              key={target.item.model.id}
              accessible={false}
              focusable={false}
              {...Platform.select({
                web: {
                  tabIndex: -1,
                  // RN Web's radio PressResponder handles Enter, but not Space.
                  onKeyDown: (event: React.KeyboardEvent) => {
                    if (event.key === ' ' && !disabled) {
                      event.preventDefault();
                      event.stopPropagation();
                      if (!event.repeat) select();
                    }
                  },
                },
              })}
              disabled={disabled}
              onPress={select}
            >
              <DNABox
                testID="custom-deck-selection-row"
                appearance="row"
                alignY="center"
                style={[styles.row, styles.deckRow, {
                  borderColor: selected ? colors['color-primary-500'] : styles.row.borderColor,
                  backgroundColor: selected ? colors['color-primary-100'] : 'transparent',
                  opacity: reason ? 0.5 : 1,
                }]}
              >
                <DNABox style={styles.deckThumbnail}>
                  <DNACustomDeckThumbnail customDeckORM={target.deck} width={64} height={42} />
                </DNABox>
                <DNABox
                  appearance="col"
                  fill
                  testID="custom-deck-selection-title-container"
                  style={styles.deckTitleContainer}
                >
                  <DNAText
                    b1
                    testID="custom-deck-selection-title"
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {target.item.meta.title}
                  </DNAText>
                </DNABox>
                <RadioItem.Kitten
                  checked={selected}
                  disabled={disabled}
                  onChange={select}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: selected, disabled }}
                  aria-checked={selected}
                  accessibilityLabel={target.item.meta.title}
                  testID="custom-deck-selection-radio"
                  style={styles.deckSelection}
                />
              </DNABox>
            </Pressable>
          );
        })}
      </DNABox>
    );
  };

  return (
    <DNAModal style={styles.modal}>
      <DNAModal.Header onClose={closeModal}>
        <DNABox appearance="col" spacing="xs" style={{ padding: 16 }}>
          <DNAText h5>Add to custom presentation</DNAText>
          <DNAText status="subtle">Adding {state.resolved.requestedCount} slides</DNAText>
        </DNABox>
      </DNAModal.Header>
      <DNABox appearance="col" fill style={styles.body}>
        <DNABox style={styles.staticContent}>
          <DNAButton
            appearance="outline"
            iconLeft="plus"
            disabled={!state.canCreate}
            onPress={() => state.openBuilder(false)}
          >Create new presentation</DNAButton>
        </DNABox>
        <DNABox
          as={ScrollView}
          appearance="col"
          fill
          testID="custom-presentation-folder-list"
          style={styles.folderList}
        >
          <DNABox appearance="col" spacing="md" style={styles.folderListContent}>
            {!state.foldersLoading && roots.map(folder => renderFolder(folder, new Set()))}
          </DNABox>
        </DNABox>
      </DNABox>
      <DNAModal.Confirmation style={styles.confirmation}>
        <DNAButton appearance="outline" status="secondary" size="md" onPress={closeModal}>Cancel</DNAButton>
        <DNAButton
          appearance="filled"
          status="primary"
          size="md"
          disabled={!state.canAdd}
          onPress={() => state.openBuilder(true)}
        >
          Add to deck
        </DNAButton>
      </DNAModal.Confirmation>
    </DNAModal>
  );
};
